import {
  BISHOP,
  BLACK,
  BLACK_BIT,
  COLOR_MASK,
  type Color,
  FLAG_DROP,
  KING,
  KNIGHT,
  PAWN,
  QUEEN,
  ROOK,
  TYPE_MASK,
  WHITE,
  WHITE_BIT,
  encodeMove,
  isDrop,
  other,
} from '../board.ts';
import { pieceDef, typeForLetter } from '../pieces.ts';
import { FenError } from '../notation.ts';
import type { MoveContext } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 16 (draft). Chess with a deck of cards (Uno-style). Every turn starts with a card:
//   1–3      make that many moves in a row (a 4 would allow Scholar's mate in one turn),
//   +2       put two of your captured pieces back on any empty squares,
//   skip     you miss your turn,
//   reverse  you and your opponent swap armies (the board flips), then you move once.
// A move that gives check ends your turn at once, a player in check treats any card as a 1,
// and nobody is skipped twice in a row. Captured pieces wait in their owner's hand
// for a +2. Cards come from the position itself (as in Lava Chess), so the computer, both
// players and replays all draw the same ones without a stored seed.
//
// ext: [active card, actions left this turn, cards drawn so far, what happened when the turn
// started (bit 1: the board flipped, bit 2: the other player was skipped), the player whose
// last turn was skipped + 1 (0: nobody)]

const CARD = 0;
const LEFT = 1;
const DRAWS = 2;
const NOTE = 3;
const SKIPPED = 4;

export const CARD_PLUS2 = 5;
export const CARD_REVERSE = 6;
export const CARD_SKIP = 7;
export const NOTE_REVERSED = 1;
export const NOTE_SKIPPED = 2;

/** The deck's mix: lots of ones, fewer big numbers, a few action cards. */
export const DECK: readonly number[] = [
  ...Array(14).fill(1),
  ...Array(7).fill(2),
  ...Array(4).fill(3),
  ...Array(4).fill(CARD_PLUS2),
  ...Array(3).fill(CARD_REVERSE),
  ...Array(3).fill(CARD_SKIP),
];

export const WILDCARD_FEN = `${STANDARD_FEN} 1 1 0 0 0 -`;

const HAND_TYPES = [QUEEN, ROOK, BISHOP, KNIGHT, PAWN];

/** Card number `n` of the game, shuffled by the position it's drawn in. */
export function drawCard(board: Int8Array, n: number): number {
  let h = Math.imul(n + 1, 0x9e3779b1) | 0;
  for (let sq = 0; sq < 64; sq++) {
    const p = board[sq];
    if (!p) continue;
    h = Math.imul(h ^ (p * 64 + sq), 0x85ebca6b);
    h ^= h >>> 13;
  }
  h = Math.imul(h ^ (h >>> 16), 0xc2b2ae35);
  h ^= h >>> 15;
  return DECK[(h >>> 0) % DECK.length];
}

export function activeCard(pos: { ext: Int32Array | null }): { card: number; left: number; note: number } {
  const ext = pos.ext;
  return { card: ext?.[CARD] ?? 1, left: ext?.[LEFT] ?? 1, note: ext?.[NOTE] ?? 0 };
}

export function handCount(pos: Position, c: Color): number {
  if (!pos.hands) return 0;
  let n = 0;
  for (const t of HAND_TYPES) n += pos.hands[c * 32 + t];
  return n;
}

const mirror = (sq: number) => (7 - (sq >> 3)) * 8 + (sq & 7);

/** Reverse: the board flips top to bottom and every piece changes colour. */
function reverseBoard(pos: Position) {
  const b = pos.board;
  const flipped = new Int8Array(64);
  for (let sq = 0; sq < 64; sq++) {
    const p = b[sq];
    if (!p) continue;
    flipped[mirror(sq)] = p & COLOR_MASK ? (p & TYPE_MASK) | (p & WHITE_BIT ? BLACK_BIT : WHITE_BIT) : p;
  }
  b.set(flipped);
  const [w, k] = pos.royals;
  pos.royals = [k >= 0 ? mirror(k) : -1, w >= 0 ? mirror(w) : -1];
  pos.castling = ((pos.castling & 3) << 2) | ((pos.castling >> 2) & 3);
  pos.ep = -1;
  if (pos.hands) {
    for (let t = 0; t < 32; t++) {
      const x = pos.hands[t];
      pos.hands[t] = pos.hands[32 + t];
      pos.hands[32 + t] = x;
    }
  }
}

export const wildcard: Variant = {
  id: 'wildcard',
  info: {
    name: 'Wild Card Chess',
    but: 'you draw a card every turn',
    tagline: 'Draw a card and do what it says: move up to three times, bring pieces back, skip a go or swap armies.',
    summary: 'Every turn starts with a **card**: move 1 to 3 times, put 2 captured pieces back, skip, or **reverse** and swap armies.',
    rules: [
      'Every turn starts with a card. A number from 1 to 3 means you make that many moves in a row.',
      'A move that gives check ends your turn straight away. When you’re in check, any card counts as a 1.',
      '+2: put two of your captured pieces back on any empty squares (pawns not on the first or last row). Nothing captured yet? Make 2 moves instead.',
      'Skip: you miss your turn (never twice in a row). Reverse: you and your opponent swap armies, then you move once.',
      'Checkmate wins. Stalemate and the 50-move rule draw.',
    ],
    accent: '#ff5a5f',
    emoji: '🃏',
    bots: ['Lucky', 'Shuffler', 'Card Shark', 'High Roller', 'The House'],
  },
  startFen: WILDCARD_FEN,
  extSize: 5,
  // Captured pieces can come back with a +2, so it's only dead once both hands are empty.
  insufficientMaterial: (pos) => handCount(pos, WHITE) + handCount(pos, BLACK) === 0,
  // The draw counter decides future cards, so it's part of the position; that also means a
  // position never truly repeats (the 50-move rule still ends long games).
  hashExt: (pos) => [pos.ext![CARD], pos.ext![LEFT], pos.ext![DRAWS], pos.ext![SKIPPED]],
  repetition: false,

  parseExtra(fields, pos) {
    const [card = '1', left = '1', draws = '0', note = '0', skipped = '0', hand = '-'] = fields;
    const ext = pos.ext!;
    const c = card === '+' ? CARD_PLUS2 : Number(card);
    if (!(c >= 1 && c <= 3) && c !== CARD_PLUS2) throw new FenError('Bad card');
    const l = Number(left);
    if (!Number.isInteger(l) || l < 1 || l > 3) throw new FenError('Bad moves left');
    ext[CARD] = c;
    ext[LEFT] = l;
    ext[DRAWS] = Math.max(0, Number(draws) | 0);
    ext[NOTE] = Number(note) & 3;
    ext[SKIPPED] = Number(skipped) === 1 || Number(skipped) === 2 ? Number(skipped) : 0;
    pos.hands = new Int8Array(64);
    if (hand !== '-') {
      for (const ch of hand) {
        const t = typeForLetter(ch);
        if (!HAND_TYPES.includes(t)) throw new FenError('Bad captured piece');
        pos.hands[(ch === ch.toUpperCase() ? WHITE : BLACK) * 32 + t]++;
      }
    }
  },

  formatExtra(pos) {
    const ext = pos.ext!;
    let hand = '';
    for (const c of [WHITE, BLACK] as Color[]) {
      for (const t of HAND_TYPES) {
        const l = pieceDef(t).letter;
        hand += (c === WHITE ? l.toUpperCase() : l).repeat(pos.hands?.[c * 32 + t] ?? 0);
      }
    }
    return [ext[CARD] === CARD_PLUS2 ? '+' : String(ext[CARD]), String(ext[LEFT]), String(ext[DRAWS]), String(ext[NOTE]), String(ext[SKIPPED]), hand || '-'];
  },

  replaceMoves(pos, out, capturesOnly) {
    if (pos.ext![CARD] !== CARD_PLUS2) return false;
    if (capturesOnly) return true;
    const base = pos.turn * 32;
    for (const t of HAND_TYPES) {
      if (pos.hands![base + t] <= 0) continue;
      for (let sq = 0; sq < 64; sq++) {
        if (pos.board[sq] !== 0) continue;
        if (t === PAWN && (sq < 8 || sq >= 56)) continue;
        out.push(encodeMove(t, sq, 0, FLAG_DROP));
      }
    }
    return true;
  },

  afterMove(ctx: MoveContext) {
    const { next, us, them, rules } = ctx;
    const ext = next.ext!;
    // An illegal move (own king left attacked) is rejected after this hook; don't let a
    // reverse hide it by flipping the board first.
    const ours = next.royals[us];
    if (ours >= 0 && rules.isAttacked(next.board, ours, them)) return;

    // Captured pieces wait in their owner's hand.
    const lost = ctx.captured & TYPE_MASK;
    if (lost && lost !== KING) next.hands![them * 32 + lost]++;

    ext[NOTE] = 0;
    const gaveCheck = rules.inCheck(next);
    const dropping = ext[CARD] === CARD_PLUS2 && isDrop(ctx.move);
    const more = ext[LEFT] - 1 > 0 && !gaveCheck && (!dropping || handCount(next, us) > 0);
    if (more) {
      ext[LEFT]--;
      next.turn = us;
      return;
    }

    // The turn passes: the next player draws, and skips hand the turn back.
    if (ext[SKIPPED] === us + 1) ext[SKIPPED] = 0;
    let to: Color = them;
    let card = 1;
    for (let i = 0; i < 6; i++) {
      card = drawCard(next.board, ext[DRAWS]++);
      next.turn = to;
      if (card !== 1 && rules.inCheck(next)) card = 1;
      if (card === CARD_SKIP && ext[SKIPPED] === to + 1) card = 1;
      if (card !== CARD_SKIP) break;
      ext[SKIPPED] = to + 1;
      ctx.fx('skip', next.royals[to] >= 0 ? next.royals[to] : 0);
      ext[NOTE] |= NOTE_SKIPPED;
      to = other(to);
      card = 1;
    }
    if (card === CARD_SKIP) card = 1;
    next.turn = to;
    if (card === CARD_REVERSE) {
      reverseBoard(next);
      ext[NOTE] |= NOTE_REVERSED;
      ctx.fx('reverse', 27);
      card = 1;
    }
    if (card === CARD_PLUS2 && handCount(next, to) === 0) card = 2;
    ext[CARD] = card;
    ext[LEFT] = card === CARD_PLUS2 ? Math.min(2, handCount(next, to)) : card;
  },

  nextTurn(ctx) {
    return ctx.next.turn;
  },

  evaluate(pos) {
    // Captured pieces might come back, but they're not on the board.
    let score = 0;
    if (pos.hands) {
      for (const t of HAND_TYPES) score -= (pos.hands[t] - pos.hands[32 + t]) * pieceDef(t).value * 0.7;
    }
    // More moves this turn is worth something.
    const ext = pos.ext!;
    const tempo = (ext[LEFT] - 1) * 40;
    return score + (pos.turn === WHITE ? tempo : -tempo);
  },
};
